// ── Networks ──────────────────────────────────────────────────────────────────
// The network a show belongs to (who made and aired it), as distinct from a watch
// PROVIDER (where you can stream it today). Discover already filters on providers via
// `with_watch_providers`; this is the other axis.
//
// Worth knowing which axis has fuel: network arrives free inside the TMDB show blob
// (348 of 512 tracked rows already carry it, every one with a logo_path), while
// provider requires the user to tell us their subscriptions — and only 9 of 35 have
// (measured 2026-07-29). So the network axis works for everyone by default.
//
// PRIMARY NETWORK ONLY — this rule is load-bearing and predates this file (it was
// solved inline in ShowDNA.jsx, audit 2026-07-22): TMDB `networks` is an ARRAY, and a
// show routinely lists two or three (broadcast plus streaming, a mid-run platform move,
// co-productions). Tallying every entry counted one show under several networks and made
// the per-network totals sum past the size of the library. Extracted here so the tally
// and any "N of your shows" badge can't drift apart.

export function primaryNetwork(show) {
  const n = (show?.networks || [])[0]
  if (!n?.name) return null
  return { id: n.id, name: n.name, logo_path: n.logo_path || null }
}

// ── Day-and-date vs. delayed-window networks ─────────────────────────────────
// TMDB's `air_date` is a SINGLE field with no concept of "which release window."
// For a pure-SVOD platform that's fine: the date a Netflix/Apple TV+/Disney+ original
// drops IS its air_date, full stop. For a broadcast/cable network it's the ORIGINAL
// LINEAR air date — the same episode may not reach a streaming catalog for weeks,
// months, or ever, on a date TMDB has no field for and no API (ours, TMDB's, JustWatch's)
// actually exposes. Confirmed industry-wide, not a NextEp gap: TMDB's own moderators
// have stated there's no plan for multi-window dates, and a TVMaze community thread
// requesting separate broadcast/streaming date fields went unresolved for years
// (research, 2026-08-04). Nobody has this data — Trakt's own "Watch Now" badge is
// undated for exactly this reason.
//
// So the fix isn't finding a date that doesn't exist — it's not CLAIMING same-day
// streaming for a network where that's not true. Real user impact: "Expedition
// Unknown aired the 29th" + a Hulu badge read as "new on Hulu the 29th" when the
// episode was Discovery-first and Hulu's copy could be weeks behind or absent
// entirely (Jason, 2026-08-04).
//
// Allowlist, not denylist: an unrecognized network defaults to "assume delayed" —
// the safer direction to be wrong in is understating urgency, not overstating it.
// HBO/Max, Hulu, Peacock, Starz deliberately excluded: all four carry a real mix of
// day-and-date originals AND licensed cable-delayed content under the same network
// tag, and TMDB gives no way to tell which is which for a given show.
const DAY_AND_DATE_NETWORKS = new Set([
  'Netflix', 'Disney+', 'Apple TV+', 'Apple TV', 'Prime Video', 'Amazon',
  'Paramount+', 'AMC+',
])

export function isDayAndDate(network) {
  return !!network?.name && DAY_AND_DATE_NETWORKS.has(network.name)
}

// [{ id, name, logo_path, count }], most-watched first, ties broken by name so the
// order is stable between renders rather than dependent on object key order.
export function networkTally(items, { min = 1, limit = 0 } = {}) {
  const by = new Map()
  for (const s of items || []) {
    const n = primaryNetwork(s)
    if (!n) continue
    // Key on name, not id: the same network occasionally appears under different ids
    // across show blobs, and the name is what the reader recognises. Keep the first id
    // and the first non-null logo we see for it.
    const prev = by.get(n.name)
    if (prev) {
      prev.count += 1
      if (!prev.logo_path && n.logo_path) prev.logo_path = n.logo_path
      if (prev.id == null && n.id != null) prev.id = n.id
    } else {
      by.set(n.name, { id: n.id ?? null, name: n.name, logo_path: n.logo_path, count: 1 })
    }
  }
  const out = [...by.values()]
    .filter(n => n.count >= min)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  return limit > 0 ? out.slice(0, limit) : out
}
